import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import PageLayout, {
  PageSection,
} from "../../../components/navigation/PageLayout";
import Icon from "../../../components/AppIcon";
import { useTenantContext } from "../../../contexts/TenantContext";
import { useAuth } from "../../../contexts/AuthContext";
import { tenantsApi } from "../../../services/api";
import {
  getDefaultEligibilityRules,
  getStrictEligibilityRules,
  EligibilityReasonCode,
  EligibilityReasonLabels,
} from "../../../utils/eligibilityCalculator";

/**
 * Converts UI settings form to the EligibilityRules shape used by the calculator.
 */
function settingsToEligibilityRules(settings) {
  return {
    requireEmployeeIsActive: true,
    requireEmployeeIsEligible: true,
    requireRoleIsEligible: true,
    requireRoleIsActive: true,
    excludeEmployeesInProbation: settings.excludeProbation,
    excludeManagementRoles: settings.excludeManagement,
    requireMinimumServiceDays: settings.minimumTenureDays > 0,
    minimumServiceDays: settings.minimumTenureDays,
    requireMinimumHoursInPeriod: settings.minimumHoursPerWeek > 0,
    minimumHoursInPeriod: settings.minimumHoursPerWeek,
    requireEmploymentStarted: true,
    excludeTerminatedEmployees: true,
  };
}

/**
 * Converts EligibilityRules back to UI settings form shape.
 */
function eligibilityRulesToSettings(rules) {
  return {
    excludeProbation: rules.excludeEmployeesInProbation,
    excludeManagement: rules.excludeManagementRoles,
    minimumTenureDays: rules.minimumServiceDays,
    minimumHoursPerWeek: rules.minimumHoursInPeriod,
  };
}

const EligibilityCalculationSettings = () => {
  const { selectedTenant, needsTenantSelection, isGlobalAdmin } =
    useTenantContext();
  const { isTenantAdmin } = useAuth();

  const [notification, setNotification] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  // Settings state - initialised from default eligibility rules
  const defaultRules = getDefaultEligibilityRules();
  const [settings, setSettings] = useState({
    // Calculation Settings
    tipPercentage: 12.5,
    applyServiceCharge: true,
    serviceChargePercentage: 12.5,
    includeDeliveryTips: false,
    excludeCashTips: false,
    roundingMethod: "nearest",

    // Eligibility Settings (mapped from EligibilityRules)
    minimumHoursPerWeek: defaultRules.minimumHoursInPeriod,
    minimumTenureDays: defaultRules.minimumServiceDays,
    includePartTime: true,
    includeCasual: true,
    excludeManagement: defaultRules.excludeManagementRoles,
    excludeProbation: defaultRules.excludeEmployeesInProbation,
    probationPeriodDays: 90,

    // Auto-sync Settings
    autoSyncEligibility: true,
    syncFrequency: "daily",
  });

  const breadcrumbs = [
    { label: "Dashboard", path: "/dashboard-overview" },
    {
      label: isGlobalAdmin ? "Tenant Settings" : "System Settings",
      path: "/system/eligibility",
    },
    { label: "Eligibility & Calculation", path: "/system/eligibility" },
  ];

  const showNotification = (message, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const handleChange = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  // Load saved settings on mount or when tenant changes
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      try {
        let response;
        if (isGlobalAdmin && selectedTenant) {
          response = await tenantsApi.getSettings(selectedTenant.id);
        } else {
          response = await tenantsApi.getCurrentSettings();
        }

        if (response.success && response.data && Object.keys(response.data).length > 0) {
          // Merge saved settings with defaults so new fields still have values
          setSettings((prev) => ({ ...prev, ...response.data }));
        }
      } catch (err) {
        // If 404 or no settings yet, just use defaults – that's fine
        console.debug("No saved eligibility settings found, using defaults");
      } finally {
        setLoading(false);
      }
    };

    // Only load when we have a tenant context
    if (!isGlobalAdmin || selectedTenant) {
      loadSettings();
    } else {
      setLoading(false);
    }
  }, [isGlobalAdmin, selectedTenant?.id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      let response;
      if (isGlobalAdmin && selectedTenant) {
        response = await tenantsApi.updateSettings(selectedTenant.id, settings);
      } else {
        response = await tenantsApi.updateCurrentSettings(settings);
      }

      if (response.success) {
        showNotification("Settings saved successfully");
      } else {
        showNotification(response.message || "Failed to save settings", "error");
      }
    } catch (err) {
      showNotification(err.message || "Failed to save settings", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    if (!confirm("Are you sure you want to reset to default settings?")) return;
    const defaults = getDefaultEligibilityRules();
    setSettings({
      tipPercentage: 12.5,
      applyServiceCharge: true,
      serviceChargePercentage: 12.5,
      includeDeliveryTips: false,
      excludeCashTips: false,
      roundingMethod: "nearest",
      minimumHoursPerWeek: defaults.minimumHoursInPeriod,
      minimumTenureDays: defaults.minimumServiceDays,
      includePartTime: true,
      includeCasual: true,
      excludeManagement: defaults.excludeManagementRoles,
      excludeProbation: defaults.excludeEmployeesInProbation,
      probationPeriodDays: 90,
      autoSyncEligibility: true,
      syncFrequency: "daily",
    });
    showNotification("Settings reset to defaults");
  };

  const handleApplyStrict = () => {
    const strict = getStrictEligibilityRules();
    const mapped = eligibilityRulesToSettings(strict);
    setSettings((prev) => ({
      ...prev,
      ...mapped,
      excludeProbation: strict.excludeEmployeesInProbation,
      excludeManagement: strict.excludeManagementRoles,
    }));
    showNotification("Strict eligibility rules applied", "success");
  };

  // Check permissions
  const canEdit = isGlobalAdmin || isTenantAdmin;

  // Select Tenant Empty State for Global Admins
  if (isGlobalAdmin && needsTenantSelection) {
    return (
      <>
        <Helmet>
          <title>Eligibility & Calculation - Rocket Tronc</title>
        </Helmet>
        <PageLayout
          title="Eligibility & Calculation Settings"
          description="Configure TRONC eligibility rules and calculation methods."
          breadcrumbs={breadcrumbs}
        >
          <div className="card p-12 text-center">
            <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <Icon name="Building2" size={40} className="text-primary" />
            </div>
            <h2 className="text-2xl font-bold mb-3">Select a Tenant</h2>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              As a Global Administrator, please select a tenant from the sidebar
              to configure their eligibility and calculation settings.
            </p>
            <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
              <Icon name="ArrowLeft" size={16} />
              <span>Use the tenant selector in the sidebar</span>
            </div>
          </div>
        </PageLayout>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>
          Eligibility & Calculation - {selectedTenant?.name || "Rocket Tronc"}
        </title>
      </Helmet>

      <PageLayout
        title="Eligibility & Calculation Settings"
        description={
          selectedTenant
            ? `Configure ${selectedTenant.name}'s TRONC eligibility rules and calculation methods.`
            : "Configure TRONC eligibility rules and calculation methods."
        }
        breadcrumbs={breadcrumbs}
      >
        {/* Notification */}
        {notification && (
          <div
            className={`mb-6 alert ${notification.type === "error" ? "alert-error" : "alert-success"}`}
          >
            <Icon
              name={
                notification.type === "error" ? "AlertCircle" : "CheckCircle"
              }
              size={20}
            />
            <span>{notification.message}</span>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="w-6 h-6 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Calculation Settings */}
            <PageSection title="TRONC Calculation">
              <div className="card">
                <div className="card-content space-y-6">
                  {/* Service Charge */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <label className="font-medium">Apply Service Charge</label>
                      <p className="text-sm text-muted-foreground">
                        Include service charge in TRONC pool
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.applyServiceCharge}
                        onChange={(e) =>
                          handleChange("applyServiceCharge", e.target.checked)
                        }
                        disabled={!canEdit}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {settings.applyServiceCharge && (
                    <div>
                      <label className="label">Service Charge Percentage</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={settings.serviceChargePercentage}
                          onChange={(e) =>
                            handleChange(
                              "serviceChargePercentage",
                              parseFloat(e.target.value) || 0,
                            )
                          }
                          disabled={!canEdit}
                          className="input w-24 text-right"
                          min="0"
                          max="100"
                          step="0.5"
                        />
                        <span className="text-muted-foreground">%</span>
                      </div>
                    </div>
                  )}

                  {/* Include Delivery Tips */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <label className="font-medium">Include Delivery Tips</label>
                      <p className="text-sm text-muted-foreground">
                        Include tips from delivery orders
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.includeDeliveryTips}
                        onChange={(e) =>
                          handleChange("includeDeliveryTips", e.target.checked)
                        }
                        disabled={!canEdit}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {/* Exclude Cash Tips */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <label className="font-medium">Exclude Cash Tips</label>
                      <p className="text-sm text-muted-foreground">
                        Only include card payments in TRONC
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.excludeCashTips}
                        onChange={(e) =>
                          handleChange("excludeCashTips", e.target.checked)
                        }
                        disabled={!canEdit}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {/* Rounding Method */}
                  <div>
                    <label className="label">Rounding Method</label>
                    <select
                      value={settings.roundingMethod}
                      onChange={(e) =>
                        handleChange("roundingMethod", e.target.value)
                      }
                      disabled={!canEdit}
                      className="input"
                    >
                      <option value="nearest">Round to nearest penny</option>
                      <option value="down">Always round down</option>
                      <option value="up">Always round up</option>
                    </select>
                    <p className="text-xs text-muted-foreground mt-1">
                      How to handle fractional pennies in distribution
                    </p>
                  </div>
                </div>
              </div>
            </PageSection>

            {/* Eligibility Settings */}
            <PageSection title="Employee Eligibility">
              <div className="card">
                <div className="card-content space-y-6">
                  {/* Minimum Hours */}
                  <div>
                    <label className="label">Minimum Hours Per Week</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={settings.minimumHoursPerWeek}
                        onChange={(e) =>
                          handleChange(
                            "minimumHoursPerWeek",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        disabled={!canEdit}
                        className="input w-24 text-right"
                        min="0"
                        max="168"
                      />
                      <span className="text-muted-foreground">hours</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Employees must work at least this many hours to be eligible
                    </p>
                  </div>

                  {/* Minimum Tenure */}
                  <div>
                    <label className="label">Minimum Tenure</label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        value={settings.minimumTenureDays}
                        onChange={(e) =>
                          handleChange(
                            "minimumTenureDays",
                            parseInt(e.target.value) || 0,
                          )
                        }
                        disabled={!canEdit}
                        className="input w-24 text-right"
                        min="0"
                      />
                      <span className="text-muted-foreground">days</span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Days of employment before becoming eligible
                    </p>
                  </div>

                  {/* Include Part-Time */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <label className="font-medium">
                        Include Part-Time Staff
                      </label>
                      <p className="text-sm text-muted-foreground">
                        Part-time employees are eligible for TRONC
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.includePartTime}
                        onChange={(e) =>
                          handleChange("includePartTime", e.target.checked)
                        }
                        disabled={!canEdit}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {/* Include Casual */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <label className="font-medium">Include Casual Staff</label>
                      <p className="text-sm text-muted-foreground">
                        Casual/zero-hours staff are eligible
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.includeCasual}
                        onChange={(e) =>
                          handleChange("includeCasual", e.target.checked)
                        }
                        disabled={!canEdit}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {/* Exclude Management */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <label className="font-medium">Exclude Management</label>
                      <p className="text-sm text-muted-foreground">
                        Management roles are not eligible
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.excludeManagement}
                        onChange={(e) =>
                          handleChange("excludeManagement", e.target.checked)
                        }
                        disabled={!canEdit}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {/* Exclude Probation */}
                  <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                    <div>
                      <label className="font-medium">
                        Exclude Probation Period
                      </label>
                      <p className="text-sm text-muted-foreground">
                        Staff on probation are not eligible
                      </p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={settings.excludeProbation}
                        onChange={(e) =>
                          handleChange("excludeProbation", e.target.checked)
                        }
                        disabled={!canEdit}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                    </label>
                  </div>

                  {settings.excludeProbation && (
                    <div>
                      <label className="label">Probation Period Length</label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          value={settings.probationPeriodDays}
                          onChange={(e) =>
                            handleChange(
                              "probationPeriodDays",
                              parseInt(e.target.value) || 0,
                            )
                          }
                          disabled={!canEdit}
                          className="input w-24 text-right"
                          min="0"
                        />
                        <span className="text-muted-foreground">days</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </PageSection>
          </div>
        )}

        {/* Auto-Sync Settings */}
        <PageSection title="Automatic Eligibility Sync" className="mt-6">
          <div className="card">
            <div className="card-content space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <label className="font-medium">Auto-Sync Eligibility</label>
                  <p className="text-sm text-muted-foreground">
                    Automatically update employee eligibility based on above
                    rules
                  </p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.autoSyncEligibility}
                    onChange={(e) =>
                      handleChange("autoSyncEligibility", e.target.checked)
                    }
                    disabled={!canEdit}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-muted peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-primary/50 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {settings.autoSyncEligibility && (
                <div>
                  <label className="label">Sync Frequency</label>
                  <select
                    value={settings.syncFrequency}
                    onChange={(e) =>
                      handleChange("syncFrequency", e.target.value)
                    }
                    disabled={!canEdit}
                    className="input max-w-xs"
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="manual">Manual Only</option>
                  </select>
                </div>
              )}
            </div>
          </div>
        </PageSection>

        {/* Action Buttons */}
        {canEdit && (
          <div className="flex justify-end gap-3 mt-6">
            <button
              className="btn btn-outline"
              onClick={handleReset}
              disabled={saving}
            >
              Reset to Defaults
            </button>
            <button
              className="btn btn-primary"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Icon name="Save" size={18} />
                  Save Settings
                </>
              )}
            </button>
          </div>
        )}
      </PageLayout>
    </>
  );
};

export default EligibilityCalculationSettings;
